import React, { CSSProperties, FC, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FocusZone, Icon, Label, Link, Stack } from "@fluentui/react";
import { ITheme, IUIItem, css } from "cayo.ui";
import styled from "styled-components";
import { AvailabilitySchedule, Entity } from "@cayo/shared/api/types/cayo-graph";
import { IJsonForm } from "@cayo/shared/api/types/schema";
import { commonMessages } from "@cayo/shared/messages/common";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import useService from "@cayo/shared/services/hooks/useService";
import availabilityScheduleFormatter from "./availability-schedule-formatter";
import messages from "./messages";
import { IInplaceAvailabilitySchedule } from "../../api/schema.api";
import { useSchemes } from "@cayo/shared/components/App/hooks";
import CollectActionParametersDialog from "../Dialogs/CollectActionParametersDialog";

type AvailabilityScheduleProps = {
  object?: Entity;
  style?: CSSProperties;
};

const InplaceAvailabilitySchedule: FC<AvailabilityScheduleProps & IInplaceAvailabilitySchedule> = (
  props
) => {
  const { object, label, style, required, serverTimeZone, selectionMode } = props;
  const { refreshPropertiesPanel } = useSchemes();
  const [actionProps, setActionProps] = useState<IJsonForm | null>(null);
  const actionBuilder = useService("actionBuilder");

  const intl = useIntl();

  let realSchedule: AvailabilitySchedule[] = (object as any)?.availability;
  const realObject: any = object;

  if (!realSchedule) {
    realSchedule = realObject?.availability;
  }

  const readableText = useMemo(() => {
    const result = availabilityScheduleFormatter.toReadableText(intl, realSchedule);

    return result;
  }, [realSchedule]);

  const showScheduleDialog = useCallback(() => {
    const url = endpoints.makeFullApiPath(realObject!.objectPath!);

    const params: IJsonForm = {
      title: intl.formatMessage(messages.subtitle),
      items: [
        {
          type: "timelimit",
          name: "availability",
          value: realSchedule,
          serverTimeZone,
          required: props.required,
          selectionMode,
        } as IUIItem,
      ],
      type: "",
      submit: {
        url,
        method: "PATCH",
        response: {
          "200,204": "close",
        },
      },
    };

    setActionProps(params);
  }, [realObject?.objectPath, realSchedule]);

  const scheduleControl = (
    <Link
      styles={{
        root: {
          textDecoration: "none !important",
          width: "100%",
        },
      }}
      onClick={showScheduleDialog}
    >
      <Stack horizontal={true} verticalAlign="center">
        <Icon iconName={"DateTime"} styles={{ root: { fontSize: 16 } }} />
        <div className={css.pl6}>{readableText}</div>
      </Stack>
    </Link>
  );

  return (
    <Stack style={style}>
      <Label required={required}>{label}</Label>
      <StyledFocusZone>{scheduleControl}</StyledFocusZone>
      {actionProps && (
        <CollectActionParametersDialog
          {...actionProps}
          onClose={() => setActionProps(null)}
          onSubmitCB={() => {
            refreshPropertiesPanel();
            setActionProps(null);
          }}
          actionBuilder={actionBuilder}
          footer={{
            okText: intl.formatMessage(commonMessages.save),
            cancelText: intl.formatMessage(commonMessages.cancel),
          }}
        />
      )}
    </Stack>
  );
};

const StyledFocusZone = styled(FocusZone)`
  border: 1px solid ${(props) => (props.theme as ITheme).officeTheme.semanticColors.buttonBorder};
  padding: 6px 10px;
`;

export default InplaceAvailabilitySchedule;
